import { EvalType } from "components/Conversations/Stores/ConversationsStore";
import BaseService from "./BaseService";
import { ITranscriptionPayload } from "./TranscriptionService";
import { IChatRecord } from "components/UI/Chat/AcxChatViewer";
import { IMultiLanguageChatTranscriptionPayload } from "./ChatMediaService";
import SoundClip from "models/SoundClip";
import { InteractionType } from "models/InteractionType";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import ConversationModule from "models/ConversationModule";
import { Answer } from "models/Answer";
import { ISignalsChartResponse } from "components/Signals/SignalsModels";
import { JsonSchemaRoot } from "components/JsonSchema/types";

export interface ConversationData {
    callDurationMillis: number;
    chatTranscription: IChatRecord[];
    conversationClassifiers: ConversationClassifier[];
    conversationId: string;
    evalType: EvalType | null;
    mediaType: InteractionType;
    summary: string;
    timestamp: string;
    transcriptions: ITranscriptionPayload[];
    agentFirstName: string;
    agentLastName: string;
    evaluationId: string;
    hasTranslation: boolean;
    nonEnglish: boolean;
    isRedacted: boolean;
    highlights: Highlight;
}

export interface SingleConversationData {
    callDurationMillis: number;
    multiLanguageChatTranscription?: IMultiLanguageChatTranscriptionPayload[];
    conversationClassifiers: ConversationClassifier[];
    conversationId: string;
    evalType: EvalType | null;
    mediaType: InteractionType;
    summary: string;
    timestamp: string;
    transcriptions: ITranscriptionPayload[];
    agentFirstName: string;
    agentLastName: string;
    evaluationId?: string;
    isRedacted: boolean;
    evaluationNumber: number;
    evaluationAgentCoachingId?: string;
    agentId: string;
    hierarchyId: string;
    callDirection: string;
    workflowInstanceIds?: string[];
    focusedConversationInsightSource?: FocusedConversationInsightSource;
}

export type FocusedConversationInsightSource = "Drivers" | "Topics" | null;

export interface ConversationSoundClip {
    soundClip: SoundClip;
    montageIds: string[] | null;
}

export type Highlight = {
    audioMetadataId: string;
    fullTextSearchHighlightsSpeaker0: string | null;
    fullTextSearchHighlightsSpeaker1: string | null;
    fullTextSearchHighlightsSpeaker2: string | null;
    fullTextSearchHighlightsSpeaker3: string | null;
    rbcHighlightResults: IRBCHighlight[];
} | null;

export interface IRBCHighlight {
    classifierName: string;
    classifierId: string;
    speaker0Highlights: string | null;
    speaker1Highlights: string | null;
    speaker2Highlights: string | null;
    speaker3Highlights: string | null;
}

export interface ConversationClassifier {
    classifierId: string;
    conversationId: string;
    name: string;
    description: string;
    result: string;
    score: number;
    classifierType: "Lucene" | "Tensorflow" | "LLM" | "ExtendedMetadata";
    resultJson: string | null;
    resultJsonSchema?: JsonSchemaRoot;
    writtenExtendedMetadata?: ConversationClassifier[];
    groupName?: string;
}

interface ConversationsPayload {
    conversationResults: ConversationData[];
    estimatedCount: number;
    sqlQueries: string[] | undefined;
    visualizationData: ISignalsChartResponse;
}

export interface VisibleEvalModule {
    id: string;
    name: string;
    moduleScore: number;
}

type ConversationsRequest = ApplicationFilters & {
    pageNumber: number;
    sortField?: "ArrivedOn" | "Timestamp" | null;
    sortDirection?: "asc" | "desc" | null;
    conversationIds?: string[];
};

type HighlightsRequest = {
    rbcFilterItems: IRBCFilter[];
    searchPhrase: string;
    conversationIds: string[];
};

export type IConversationModuleInfo = {
    answers: Answer[];
    conversationModuleId: string;
    licensedModuleId: string;
};

export type ParsedSegments = Record<string, [number, number][]>;

export interface ConversationSegmentItem {
    id: string;
    heading: string;
    startWordIndex: number;
    endWordIndex: number;
}
export type CreateConversationCommentRequest = {
    audioMetadataId: string;
    comment: string;
    audioTimeStamp?: number;
    parentCommentId?: string;
    taggedUsers: string[];
};

export type UpdateConversationCommentRequest = {
    commentId: string;
    comment: string;
    taggedUsers: string[];
};

export type ConversationCommentReaction = {
    id?: string;
    type?: string;
    createdOn?: string;
};

export type ConversationCommentUser = {
    id?: string;
    name?: string;
    email?: string;
};

export type ConversationComment = {
    id?: string;
    createdOn?: string;
    modifiedOn?: string;
    creatorUserId: string;
    creatorUser?: ConversationCommentUser;
    creatorUserName?: string;
    audioMetadataId: string;
    parentCommentId?: string;
    comment: string;
    commentAudioTimestamp?: number;
    taggedUserIds: string[];
    reactions: ConversationCommentReaction[];
    childrenComments?: ConversationComment[];
};

export class ConversationService extends BaseService {
    protected urlBase = "api/Conversations";

    public async getConversations(
        request: ConversationsRequest,
        signal?: AbortSignal,
    ): Promise<ConversationsPayload> {
        const urlParams = new URLSearchParams();
        const apiPath = "GetConversations";

        const res = await this.post<ConversationsPayload>(
            JSON.stringify(request),
            urlParams,
            apiPath,
            undefined,
            undefined,
            signal,
        );

        return res;
    }

    public async getHighlights(
        request: HighlightsRequest,
        signal?: AbortSignal,
    ): Promise<Highlight[]> {
        const urlParams = new URLSearchParams();
        const apiPath = "GetHighlights";

        const res = await this.post<Highlight[]>(
            JSON.stringify(request),
            urlParams,
            apiPath,
            undefined,
            undefined,
            signal,
        );

        return res;
    }

    public async getConversation(
        conversationId: string,
        signal?: AbortSignal,
    ): Promise<SingleConversationData> {
        const params = new URLSearchParams({
            conversationId,
        });

        const res = await this.get<SingleConversationData>(
            params,
            "GetSingleConversationAsync",
            signal,
        );

        return res;
    }

    public async getConversationSoundClipByAudioMetadataId(
        audioMetadataId: string,
    ) {
        const res = await this.get<ConversationSoundClip[]>(
            new URLSearchParams({ audioMetadataId }),
            "GetSoundClipsAndMontageIdsByAudioMetadataId",
        );
        return res;
    }

    public async getVisibleEvalModulesForCompletedEvaluation(
        evaluationId: string,
    ) {
        const res = await this.get<VisibleEvalModule[]>(
            new URLSearchParams({ evaluationId }),
            "GetVisibleEvalModulesForCompletedEvaluation",
        );

        return res;
    }

    public async getScreenRecordings(amdId: string) {
        const res = await this.get<any>(
            new URLSearchParams({ amdId }),
            "GetScreenRecordings",
        );

        return res;
    }

    public async getClassifierResults(amdId: string) {
        const res = await this.get<ConversationClassifier[]>(
            new URLSearchParams({ amdId }),
            "GetClassifierResults",
        );

        return res;
    }

    public async getExtractedMetadata(
        conversationId: string,
        classifierId: string,
    ): Promise<ConversationClassifier[]> {
        const params = new URLSearchParams({ conversationId, classifierId });
        return await this.get<ConversationClassifier[]>(
            params,
            "GetExtractedMetadata",
        );
    }

    public async createConversationModule(request: {
        conversationModule: ConversationModule;
        answers: Answer[];
    }): Promise<{ conversationModule: ConversationModule; answers: Answer[] }> {
        const urlParams = new URLSearchParams();
        const apiPath = "CreateConversationModule";

        const res = await this.post<{
            conversationModule: ConversationModule;
            answers: Answer[];
        }>(JSON.stringify(request), urlParams, apiPath);

        return res;
    }

    public async updateConversationModule(request: {
        conversationModuleId: string;
        answers: Answer[];
    }) {
        const urlParams = new URLSearchParams();
        const apiPath = "UpdateConversationModule";

        const res = await this.put(urlParams, apiPath, JSON.stringify(request));

        return res;
    }

    public async getConversationModuleInfo(
        amdId: string,
    ): Promise<IConversationModuleInfo[]> {
        return this.get(
            new URLSearchParams({ amdId }),
            "GetConversationModuleInfo",
        );
    }

    public async getConversationSegments(conversationId: string) {
        return this.get<ParsedSegments>(
            new URLSearchParams({ conversationId }),
            "GetConversationSegments",
        );
    }

    public async getConversationComments(
        amdId: string,
    ): Promise<ConversationComment[]> {
        return this.get(
            new URLSearchParams({ amdId }),
            "GetConversationComments",
        );
    }

    public async createConversationComment(
        request: CreateConversationCommentRequest,
    ): Promise<string> {
        const urlParams = new URLSearchParams();
        const apiPath = "CreateConversationComment";

        return this.post<string>(JSON.stringify(request), urlParams, apiPath);
    }

    public async updateConversationComment(
        request: UpdateConversationCommentRequest,
    ): Promise<void> {
        const urlParams = new URLSearchParams();
        const apiPath = "EditConversationComment";

        await this.put(urlParams, apiPath, JSON.stringify(request));
    }

    public async deleteConversationComment(commentId: string): Promise<void> {
        await this.delete(
            new URLSearchParams({ commentId }),
            "DeleteConversationComment",
        );
    }
}

// used to get error message for failing searchPhrases from getConversations
export function extractOtherDetails(errorMessage: string): string | null {
    const detailsKeyword = `"otherDetails":`;
    const innerKeyword = "inner";
    const parts = errorMessage.split(detailsKeyword);
    if (parts.length > 1) {
        const finalPart = parts[1].split(innerKeyword)[0];
        return finalPart.trim();
    }
    return "";
}
